import React, { forwardRef, Ref, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

import FontAwesome from '@vromo/ui/components/FontAwesome';
import Modal, { ModalContent } from '@vromo/ui/components/Modal';
import { css } from 'emotion';

import { Vehicle, Widget } from '@vromo/models';
import { Job } from '@vromo/models';
import { when } from 'mobx';
import Color from 'color';
import takeRight from 'lodash/takeRight';
import styled from '@emotion/styled';
import { Graph } from '@vromo/api/graph';
import Geocoder, { GeocoderValue } from '@vromo/ui/components/Geocoder2';
import { Task } from '@vromo/models';
import { Theme } from '@emotion/react';

const PopupSubmit = styled.button`
    font-family: 'aktiv-grotesk', 'Helvetica Neue', 'Helvetica', 'Arial', 'Open Sans', sans-serif;
    flex: 1;
    margin-top: 14px;
    padding: 10px 14px;
    align-self: center;
    background-color: ${({ theme }) =>
        Color(theme.background).isLight() ? theme.accent : theme.background};
    border: none;
    color: ${({ theme }) => (Color(theme.background).isLight() ? '#111111' : 'white')};
    border-radius: 5px;
    cursor: pointer;
    font-size: 14px;
    font-weight: 600;

    &:first-child:not(:only-child) {
        margin-right: 10px;
    }
`;

interface WidgetProps {
    widget: Widget;
    theme: Theme;
    vehicle: Vehicle;
    job: Job;
    submit: (newAddress: GeocoderValue) => void;
    submitted: boolean;
    host?: string;
}

const AddressCorrectionPopup = forwardRef(function Popup(
    { widget, theme, job, submit, submitted }: WidgetProps,
    ref: Ref<HTMLDivElement>
) {
    const [open, setOpen] = useState(false);
    const [newAddress, setNewAddress] = useState<GeocoderValue | null>(null);
    const [step, setStep] = useState<string>('confirm');
    const [success, setSuccess] = useState<boolean>(true);

    useEffect(() => {
        return when(
            () => {
                switch (widget.settings.when) {
                    case 'dispatched':
                        return job.status === 'dispatched';
                    case 'underway':
                        return job.status === 'underway' && job.progress === 0;
                    case 'completed':
                        return job.status === 'completed';
                    case 'cancelled':
                        return job.status === 'cancelled';
                    case 'task0':
                        return job.progress === 1 && job.status === 'underway';
                    case 'task1':
                        return job.progress === 2 && job.status === 'underway';
                    case 'task2':
                        return job.progress === 3 && job.status === 'underway';
                    case 'task3':
                        return job.progress === 4 && job.status === 'underway';
                    default:
                        return false;
                }
            },
            () => {
                setOpen(true);
            }
        );
    }, []);

    function renderModal(step: string) {
        switch (step) {
            case 'confirm':
                return (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center'
                        }}>
                        <FontAwesome
                            icon="map-marked-alt"
                            weight="duotone"
                            style={{
                                color: Color(theme.background).isLight()
                                    ? theme.accent
                                    : theme.background,
                                padding: 10
                            }}
                            x5
                        />
                        <h2
                            className={css`
                                font-weight: 400;
                                margin: 0 0 4px;
                                font-size: 18px;
                                color: #111111;
                                padding: 10px;
                            `}>
                            Is this address correct?
                        </h2>
                        <div
                            className={css`
                                display: flex;
                                font-weight: 600;
                            `}>
                            {job.tasks.last()?.zone?.address}
                        </div>

                        <div
                            style={{
                                display: 'flex',
                                width: '100%',
                                justifyContent: 'space-around'
                            }}>
                            <PopupSubmit
                                onClick={() => {
                                    setStep('update');
                                }}>
                                No
                            </PopupSubmit>
                            <PopupSubmit
                                onClick={() => {
                                    setOpen(false);
                                }}>
                                Yes
                            </PopupSubmit>
                        </div>
                    </div>
                );
            case 'update':
                return (
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center'
                        }}>
                        <FontAwesome
                            icon="map-marker-edit"
                            weight="duotone"
                            style={{
                                color: Color(theme.background).isLight()
                                    ? theme.accent
                                    : theme.background,
                                padding: 10
                            }}
                            x5
                        />
                        <h2
                            className={css`
                                font-weight: 400;
                                margin: 0 0 4px;
                                font-size: 18px;
                                color: #111111;
                            `}>
                            Update your address below:
                        </h2>
                        <div
                            className={css`
                                width: 100%;
                                padding: 10px 0px;

                                input {
                                    font-size: 12px;
                                }
                            `}>
                            <Geocoder
                                placeholder="Search for an address or place..."
                                onInputChange={() => setNewAddress(null)}
                                onChange={(result) => {
                                    if (result) {
                                        setNewAddress(result);
                                    }
                                }}
                                proximity={job.tasks?.first()?.zone?.coords}
                            />
                        </div>
                        <div
                            style={{
                                display: 'flex',
                                width: '100%',
                                justifyContent: 'space-around'
                            }}>
                            <PopupSubmit
                                onClick={() => {
                                    setStep('confirm');
                                    setNewAddress(null);
                                }}>
                                Go Back
                            </PopupSubmit>
                            <PopupSubmit
                                disabled={newAddress === null}
                                onClick={() => {
                                    setOpen(false);
                                    submit(newAddress!);
                                }}>
                                Submit
                            </PopupSubmit>
                        </div>
                    </div>
                );
        }
    }

    function renderSuccessModal() {
        return (
            <Modal isOpen={success && submitted}>
                <ModalContent>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            textAlign: 'center'
                        }}>
                        <FontAwesome
                            icon="map-marker-check"
                            weight="duotone"
                            style={{ color: 'green', padding: 10 }}
                            x5
                        />
                        <h2
                            className={css`
                                font-weight: 400;
                                margin: 0 0 4px;
                                font-size: 18px;
                                color: #111111;
                            `}>
                            Your address has been successfully updated!
                        </h2>
                        <div
                            style={{
                                display: 'flex',
                                width: '100%',
                                justifyContent: 'space-around'
                            }}>
                            <PopupSubmit
                                disabled={newAddress === null}
                                onClick={() => {
                                    setStep('confirm');
                                    setSuccess(false);
                                    setOpen(false);
                                }}>
                                OK
                            </PopupSubmit>
                        </div>
                    </div>
                </ModalContent>
            </Modal>
        );
    }

    return (
        <>
            <Modal isOpen={open}>
                <ModalContent ref={ref} style={{ overflow: 'visible' }}>
                    {renderModal(step)}
                </ModalContent>
            </Modal>
            {renderSuccessModal()}
        </>
    );
});

export default observer<{ auth: string } & WidgetProps>(
    function AddressCorrection(
        { widget, theme, vehicle, job, host, auth },
        ref: Ref<HTMLDivElement>
    ) {

        const [submitted, setSubmitted] = useState(false);
        const graph = new Graph(host)

        async function submitChanges(geocoded: GeocoderValue) {

            // Replace last two tasks if they have the same address
            const replaceIndexes = takeRight(job.tasks!.items, 2)
                .filter(
                    task =>
                        task.zone?.address === job.tasks!.last()?.zone?.address &&
                        task.ix !== null
                )
                .map((t: Task) => t.ix!);

            await Promise.all(replaceIndexes.map(async (ix) => {
                const { radius, type } = job.tasks!.items[ix]!.zone!

                const taskChanges = {} as Task
                taskChanges.zone = {
                    address: geocoded.label,
                    name: geocoded.label,
                    radius,
                    type,
                    coords: geocoded.value,
                    id: null,
                    created: null
                }

                return graph.root.track(auth, job.id!)
                    .job()
                    .tasks(ix)
                    .post(taskChanges);
            }))

            setSubmitted(true)
        }

        return (
            <AddressCorrectionPopup
                ref={ref}
                widget={widget}
                theme={theme}
                vehicle={vehicle}
                job={job}
                submit={submitChanges}
                submitted={submitted}
            />
        );
    },
    {
        forwardRef: true
    }
);
